import { cn } from "@/lib/utils";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import type { SortDir, StatusFilter } from "@/lib/use-table-controls";

/**
 * Clickable sort header for hand-rolled `<table>` pages. Renders the label
 * plus a chevron indicator: up/down when this column is the active sort,
 * a faint up-down glyph when sortable-but-inactive. Render inside a `<th>`.
 */
export function SortHeaderButton({
    label,
    active,
    dir,
    onClick,
    className,
}: {
    label: string;
    active: boolean;
    dir: SortDir;
    onClick: () => void;
    className?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={`Sort by ${label}`}
            className={cn(
                "inline-flex items-center gap-1 font-medium hover:text-foreground",
                // The ordering column reads at a glance, not only by its chevron.
                active && "text-primary",
                className,
            )}
        >
            {label}
            {active ? (
                dir === "asc" ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                )
            ) : (
                <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
            )}
        </button>
    );
}

/**
 * Active / Inactive / All segmented toggle matching the Slice 5 styling:
 * a bordered button group where the selected option is filled with the
 * primary color. Labels are configurable (e.g. "Archived" instead of
 * "Inactive").
 */
export function StatusFilterToggle({
    value,
    onChange,
    labels,
    className,
}: {
    value: StatusFilter;
    onChange: (s: StatusFilter) => void;
    labels?: Partial<Record<StatusFilter, string>>;
    className?: string;
}) {
    const resolved: Record<StatusFilter, string> = {
        active: labels?.active ?? "Active",
        inactive: labels?.inactive ?? "Inactive",
        all: labels?.all ?? "All",
    };
    return (
        <div className={cn("inline-flex rounded-lg border overflow-hidden text-sm", className)}>
            {(["active", "inactive", "all"] as StatusFilter[]).map((opt) => (
                <button
                    key={opt}
                    type="button"
                    onClick={() => onChange(opt)}
                    className={cn(
                        "px-3 py-2",
                        value === opt ? "bg-primary text-primary-foreground" : "hover:bg-accent",
                    )}
                >
                    {resolved[opt]}
                </button>
            ))}
        </div>
    );
}
